import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import api from "../config/api";
import toast from "react-hot-toast";
import { COUNTRIES, TIMEZONES, detectBrowserTimezone } from "../constants/geo";

// ── Auth step for unauthenticated visitors ────────────────────────────────────
type AuthStep = "email" | "register" | "magic-link-sent";

const AcceptInvite: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = decodeURIComponent(searchParams.get("token") || "");
  const { isAuthenticated, user, checkEmail, register, requestMagicLink } =
    useAuth();

  // ── Invite info ───────────────────────────────────────────────────────────
  const [inviteInfo, setInviteInfo] = useState<{
    email: string;
    role: string;
    company_name: string;
    invited_by_name: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Accept state ──────────────────────────────────────────────────────────
  const [accepting, setAccepting] = useState(false);
  const [existingItemCount, setExistingItemCount] = useState<number>(0);
  const [showWarning, setShowWarning] = useState(false);
  const [autoAccepting, setAutoAccepting] = useState(
    // Show spinner immediately if we know auto-accept will run on mount
    () => isAuthenticated && !!localStorage.getItem("auto_accept_invite")
  );

  // ── Inline auth state (unauthenticated flow) ──────────────────────────────
  const [authStep, setAuthStep] = useState<AuthStep>("email");
  const [inputEmail, setInputEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [checking, setChecking] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [country, setCountry] = useState("");
  const [timezone, setTimezone] = useState(() => detectBrowserTimezone());
  const [submitting, setSubmitting] = useState(false);

  // ── Load invite details ───────────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setError("Invalid invite link.");
      setLoading(false);
      return;
    }
    api
      .get(`/team/invite-info?token=${token}`)
      .then((res) => setInviteInfo(res.data))
      .catch((err) =>
        setError(err.response?.data?.error || "Invite not found or expired")
      )
      .finally(() => setLoading(false));
  }, [token]);

  // ── Pre-fill email field with invited email once we have it ───────────────
  useEffect(() => {
    if (inviteInfo?.email && !inputEmail) {
      setInputEmail(inviteInfo.email);
    }
  }, [inviteInfo?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── If already authenticated, fetch item count then auto-accept if flagged ─
  useEffect(() => {
    if (!isAuthenticated) return;
    api
      .get("/expiry")
      .then((res) => {
        const count = Array.isArray(res.data) ? res.data.length : 0;
        setExistingItemCount(count);

        // Auto-accept when user returns via magic link from the invite flow.
        // Consume the flag immediately so navigating back doesn't re-trigger.
        const autoAccept = localStorage.getItem("auto_accept_invite");
        if (!autoAccept) return;
        localStorage.removeItem("auto_accept_invite");

        if (count > 0) {
          // Has existing items — show warning, don't silently accept
          setAutoAccepting(false);
          setShowWarning(true);
        } else {
          doAccept();
        }
      })
      .catch(() => setExistingItemCount(0));
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Inline auth: Step 1 — email submitted ─────────────────────────────────
  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError("");
    const trimmed = inputEmail.trim().toLowerCase();

    if (inviteInfo && trimmed !== inviteInfo.email.toLowerCase()) {
      setEmailError(
        `This invite is for ${inviteInfo.email}. Please use that email to continue.`
      );
      return;
    }

    setChecking(true);
    try {
      const { exists } = await checkEmail(trimmed);
      if (exists) {
        // Existing user — send magic link, come back to accept page after
        const returnUrl = `/team/accept?token=${encodeURIComponent(token)}`;
        localStorage.setItem("post_login_redirect", returnUrl);
        localStorage.setItem("auto_accept_invite", "1");
        await requestMagicLink(trimmed);
        setAuthStep("magic-link-sent");
      } else {
        // New user — collect name inline
        setAuthStep("register");
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Something went wrong");
    } finally {
      setChecking(false);
    }
  };

  // ── Inline auth: Step 2 (new user) — register + send magic link ───────────
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim()) {
      toast.error("First name is required");
      return;
    }
    if (!lastName.trim()) {
      toast.error("Last name is required");
      return;
    }

    setSubmitting(true);
    try {
      // Register without company_name — they'll join the inviting company
      await register({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: inputEmail.trim().toLowerCase(),
        country: country || undefined,
        timezone: timezone || undefined,
      });
      // Store redirect so magic link brings them back here, then auto-accepts
      const returnUrl = `/team/accept?token=${encodeURIComponent(token)}`;
      localStorage.setItem("post_login_redirect", returnUrl);
      localStorage.setItem("auto_accept_invite", "1");
      setAuthStep("magic-link-sent");
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to create account");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Accept invite (authenticated path) ───────────────────────────────────
  const handleAcceptClick = () => {
    if (existingItemCount > 0) {
      setShowWarning(true);
      return;
    }
    doAccept();
  };

  const doAccept = async () => {
    setAccepting(true);
    setShowWarning(false);
    try {
      const res = await api.post("/team/accept", { token });
      if (res.data.token) localStorage.setItem("token", res.data.token);
      if (res.data.user)
        localStorage.setItem("user", JSON.stringify(res.data.user));
      toast.success("You've joined the team!");
      window.location.href = "/items";
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to accept invite");
    } finally {
      setAccepting(false);
    }
  };

  // ── Shared styles ─────────────────────────────────────────────────────────
  const inputCls =
    "appearance-none block w-full px-4 py-3 border border-[#E5E7EB] " +
    "placeholder:text-[#9CA3AF] text-[#111827] rounded-lg text-sm transition-colors " +
    "focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent";

  const selectCls =
    "appearance-none block w-full px-4 py-3 pr-10 border border-[#E5E7EB] " +
    "text-[#111827] bg-white rounded-lg text-sm transition-colors cursor-pointer " +
    "focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent " +
    "bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236B7280' d='M1 1l5 5 5-5'/%3E%3C/svg%3E\")] " +
    "bg-no-repeat bg-[right_14px_center]";

  // ── Loading / Error screens ───────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#2563EB] border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 max-w-md w-full text-center shadow-sm">
          <div className="text-4xl mb-4">😕</div>
          <h1 className="text-lg font-bold text-[#111827] mb-2">
            Invite Not Found
          </h1>
          <p className="text-sm text-[#6B7280] mb-6">{error}</p>
          <a
            href="/"
            className="px-4 py-2 text-sm font-medium text-[#2563EB] hover:underline"
          >
            Go to Home
          </a>
        </div>
      </div>
    );
  }

  // ── Invite header (shared between all states) ─────────────────────────────
  const InviteHeader = () => (
    <>
      <div className="w-14 h-14 rounded-full bg-[#EFF6FF] flex items-center justify-center mx-auto mb-5">
        <svg
          className="w-7 h-7 text-[#2563EB]"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      </div>
      <h1 className="text-xl font-bold text-[#111827] text-center mb-1">
        You're invited! 🎉
      </h1>
      <p className="text-sm text-[#6B7280] text-center mb-5">
        <strong className="text-[#111827]">
          {inviteInfo?.invited_by_name}
        </strong>{" "}
        invited you to join{" "}
        <strong className="text-[#111827]">{inviteInfo?.company_name}</strong>
      </p>
      <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-4 mb-5 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-[#6B7280]">Invited email</span>
          <span className="font-medium text-[#111827]">
            {inviteInfo?.email}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[#6B7280]">Company</span>
          <span className="font-medium text-[#111827]">
            {inviteInfo?.company_name}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[#6B7280]">Role</span>
          <span className="font-semibold text-[#2563EB] capitalize">
            {inviteInfo?.role}
          </span>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4 py-12">
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 max-w-md w-full shadow-sm">
        {/* ══════════════════════════════════════════════════════════════════
            AUTHENTICATED — warning confirmation screen
        ══════════════════════════════════════════════════════════════════ */}
        {isAuthenticated && showWarning ? (
          <>
            <div className="w-14 h-14 rounded-full bg-[#FEF2F2] flex items-center justify-center mx-auto mb-5">
              <svg
                className="w-7 h-7 text-[#DC2626]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-[#111827] text-center mb-2">
              Your existing workspace will be left behind
            </h2>
            <p className="text-sm text-[#6B7280] text-center mb-5">
              You have{" "}
              <strong className="text-[#111827]">
                {existingItemCount} document{existingItemCount !== 1 ? "s" : ""}
              </strong>{" "}
              in your personal workspace. Joining{" "}
              <strong className="text-[#111827]">
                {inviteInfo?.company_name}
              </strong>{" "}
              means you will no longer have access to those documents.
            </p>
            <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-4 py-3 mb-6 text-xs text-[#B91C1C] space-y-1">
              <p className="font-semibold">What this means:</p>
              <ul className="list-disc list-inside space-y-0.5 opacity-90">
                <li>
                  Your {existingItemCount} existing document
                  {existingItemCount !== 1 ? "s" : ""} will not be accessible
                </li>
                <li>
                  You will see {inviteInfo?.company_name}'s shared workspace
                  instead
                </li>
                <li>This cannot be undone without admin assistance</li>
              </ul>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={doAccept}
                disabled={accepting}
                className="w-full py-3 text-sm font-semibold text-white bg-[#DC2626] rounded-xl
                           hover:bg-[#B91C1C] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {accepting ? "Joining…" : "Yes, join and leave my workspace"}
              </button>
              <button
                onClick={() => setShowWarning(false)}
                disabled={accepting}
                className="w-full py-2.5 text-sm font-medium text-[#374151] bg-white border border-[#E5E7EB]
                           rounded-xl hover:bg-[#F9FAFB] disabled:opacity-50 transition-colors"
              >
                Cancel — keep my current workspace
              </button>
            </div>
          </>
        ) : isAuthenticated && autoAccepting ? (
          <>
            <div className="w-14 h-14 rounded-full bg-[#EFF6FF] flex items-center justify-center mx-auto mb-5">
              <svg
                className="animate-spin w-7 h-7 text-[#2563EB]"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-[#111827] text-center mb-2">
              Joining the team…
            </h2>
            <p className="text-sm text-[#6B7280] text-center">
              Setting up your access to{" "}
              <strong>{inviteInfo?.company_name}</strong>
            </p>
          </>
        ) : isAuthenticated ? (
          <>
            <InviteHeader />

            {/* Wrong account hard-block */}
            {user?.email !== inviteInfo?.email ? (
              <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800 space-y-2">
                <p>
                  <strong>Wrong account.</strong> You're signed in as{" "}
                  <strong>{user?.email}</strong>, but this invite is for{" "}
                  <strong>{inviteInfo?.email}</strong>.
                </p>
                <p>
                  Please{" "}
                  <button
                    onClick={() => {
                      localStorage.removeItem("token");
                      localStorage.removeItem("user");
                      const returnUrl = `/team/accept?token=${encodeURIComponent(token)}`;
                      window.location.href = `/team/accept?token=${encodeURIComponent(token)}`;
                      localStorage.setItem("post_login_redirect", returnUrl);
                    }}
                    className="underline font-semibold text-red-700 hover:text-red-900"
                  >
                    sign out
                  </button>{" "}
                  and use the invited email to continue.
                </p>
              </div>
            ) : (
              /* Soft warning if they have existing items */
              existingItemCount > 0 && (
                <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-2">
                  <svg
                    className="w-4 h-4 flex-shrink-0 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                    />
                  </svg>
                  <span>
                    You have{" "}
                    <strong>
                      {existingItemCount} document
                      {existingItemCount !== 1 ? "s" : ""}
                    </strong>{" "}
                    in your current workspace. Joining will switch you to{" "}
                    {inviteInfo?.company_name}'s workspace — you'll be asked to
                    confirm.
                  </span>
                </div>
              )
            )}

            <button
              onClick={handleAcceptClick}
              disabled={accepting || user?.email !== inviteInfo?.email}
              className="w-full py-3 text-sm font-semibold text-white bg-[#2563EB] rounded-xl
                         hover:bg-[#1D4ED8] disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors shadow-sm"
            >
              {accepting ? "Joining…" : "Accept & Join Team"}
            </button>

            <p className="text-xs text-[#9CA3AF] text-center mt-4">
              By accepting, you'll be added to {inviteInfo?.company_name}'s
              workspace.
            </p>
          </>
        ) : authStep === "magic-link-sent" ? (
          <>
            <div className="w-14 h-14 rounded-full bg-[#DCFCE7] flex items-center justify-center mx-auto mb-5">
              <svg
                className="w-7 h-7 text-[#16A34A]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-[#111827] text-center mb-2">
              Check your inbox
            </h2>
            <p className="text-sm text-[#6B7280] text-center mb-1">
              We sent a sign-in link to
            </p>
            <p className="text-sm font-semibold text-[#111827] text-center mb-6">
              {inputEmail}
            </p>

            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl p-4 mb-6 flex items-start gap-3">
              <svg
                className="w-5 h-5 text-[#16A34A] flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm text-[#15803D]">
                Click the link in your email to sign in — then you'll be brought
                back here to complete joining{" "}
                <strong>{inviteInfo?.company_name}</strong>. The link expires in{" "}
                <strong>15 minutes</strong>.
              </p>
            </div>

            <button
              onClick={() => {
                setAuthStep("email");
                setEmailError("");
              }}
              className="w-full py-2.5 text-sm font-medium text-[#374151] bg-white border border-[#E5E7EB]
                         rounded-xl hover:bg-[#F9FAFB] transition-colors"
            >
              ← Use a different email
            </button>
          </>
        ) : authStep === "register" ? (
          <>
            <InviteHeader />

            <div className="flex items-start gap-3 px-4 py-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl text-sm text-[#1D4ED8] mb-5">
              <svg
                className="w-5 h-5 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <span>
                New to the app? Fill in your name and we'll create your account
                and send a sign-in link — no password needed.
              </span>
            </div>

            <form onSubmit={handleRegister} className="space-y-4">
              {/* Email — read-only, pre-filled */}
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  value={inputEmail}
                  disabled
                  className="appearance-none block w-full px-4 py-3 border border-[#E5E7EB]
                             bg-[#F3F4F6] text-[#9CA3AF] rounded-lg text-sm cursor-not-allowed"
                />
              </div>

              {/* Name row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1.5">
                    First name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    autoFocus
                    className={inputCls}
                    placeholder="Jane"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1.5">
                    Last name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    className={inputCls}
                    placeholder="Smith"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>

              {/* Country (optional) */}
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1.5">
                  Country{" "}
                  <span className="text-[#9CA3AF] font-normal">(optional)</span>
                </label>
                <select
                  className={selectCls}
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value);
                    const found = COUNTRIES.find(
                      (c) => c.code === e.target.value
                    );
                    if (found) setTimezone(found.timezone);
                  }}
                >
                  <option value="">— Select country —</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2 pt-1">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 text-sm font-semibold text-white bg-[#2563EB] rounded-xl
                             hover:bg-[#1D4ED8] disabled:opacity-50 disabled:cursor-not-allowed
                             transition-colors flex items-center justify-center gap-2"
                >
                  {submitting && (
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  )}
                  {submitting
                    ? "Creating account…"
                    : "Create Account & Get Sign-in Link"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAuthStep("email");
                    setEmailError("");
                  }}
                  className="w-full py-2.5 text-sm font-medium text-[#374151] bg-white border border-[#E5E7EB]
                             rounded-xl hover:bg-[#F9FAFB] transition-colors"
                >
                  ← Back
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <InviteHeader />

            <div className="border-t border-[#E5E7EB] pt-5">
              <p className="text-sm font-medium text-[#374151] mb-4 text-center">
                Enter your email to accept this invite
              </p>

              <form onSubmit={handleEmailContinue} className="space-y-3">
                <div>
                  <input
                    type="email"
                    required
                    autoFocus
                    className={inputCls}
                    placeholder={inviteInfo?.email || "you@company.com"}
                    value={inputEmail}
                    onChange={(e) => {
                      setInputEmail(e.target.value);
                      setEmailError("");
                    }}
                  />
                  {emailError && (
                    <p className="mt-1.5 text-xs text-red-600">{emailError}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={checking}
                  className="w-full py-3 text-sm font-semibold text-white bg-[#2563EB] rounded-xl
                             hover:bg-[#1D4ED8] disabled:opacity-50 disabled:cursor-not-allowed
                             transition-colors flex items-center justify-center gap-2"
                >
                  {checking && (
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  )}
                  {checking ? "Checking…" : "Continue"}
                </button>
              </form>

              <p className="text-xs text-[#9CA3AF] text-center mt-4">
                We'll send a magic link — no password needed.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;
